/**
 * Loved-One Voices Service
 *
 * A loved-one voice is cloned from a relative's recording so a story can be
 * narrated in their voice. Creating one requires their explicit, recorded
 * consent — the API rejects the request without it.
 *
 * Note what is absent from these types: the provider's voice id. The app only
 * ever refers to a voice by our own row id.
 */

import { apiClient } from '@/integrations/api';
import { apiGet } from '@/lib/api';
import { handleError } from '@/lib/errors';

const CONTEXT = 'familyVoices';

export interface FamilyVoice {
  id: string;
  family_member_id: string;
  family_member_name: string | null;
  display_name: string;
  status: 'pending' | 'ready' | 'failed' | 'revoked';
  consent_given_by: string;
  consent_at: string;
  created_at: string;
}

export interface FamilyVoiceList {
  /** False when the server has no voice provider configured. */
  configured: boolean;
  /** The exact wording the relative must agree to, served by the backend. */
  consent_statement: string;
  items: FamilyVoice[];
}

export async function listFamilyVoices(): Promise<FamilyVoiceList> {
  return apiGet<FamilyVoiceList>('/family-voices', `${CONTEXT}.list`);
}

export interface CreateFamilyVoiceInput {
  familyMemberId: string;
  /** 1–5 audio recordings of the person speaking. */
  samples: File[];
  /** Name typed by the person whose voice it is. */
  consentGivenBy: string;
}

export async function createFamilyVoice(input: CreateFamilyVoiceInput): Promise<FamilyVoice> {
  const form = new FormData();
  for (const file of input.samples) form.append('samples', file);
  form.append('consentGivenBy', input.consentGivenBy);
  form.append('consentAgreed', 'true');

  try {
    const { data } = await apiClient.post<FamilyVoice>(
      `/family-voices/${input.familyMemberId}`,
      form
    );
    return data;
  } catch (error) {
    throw handleError(error, { context: `${CONTEXT}.create`, strategy: 'throw' });
  }
}

/** Revoke consent: deletes the voice at the provider and here. */
export async function deleteFamilyVoice(id: string): Promise<void> {
  try {
    await apiClient.delete(`/family-voices/${id}`);
  } catch (error) {
    throw handleError(error, { context: `${CONTEXT}.delete`, strategy: 'throw' });
  }
}

export const familyVoicesService = {
  listFamilyVoices,
  createFamilyVoice,
  deleteFamilyVoice,
};
